import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import moment from 'moment';
import 'moment/locale/es';
import axios from 'axios';
import styled from 'styled-components';
import PopupLayout from "layouts/PopupLayout";
import TrSkeleton from "components/TrSkeleton";
import { formatNumber } from 'helpers/generic';
import { getCommunity } from 'helpers/community';
import EmpoweredSelector from 'components/EmpoweredSelector';
import ExportModal from 'components/ExportModal';

const Table = styled.table`
	& > thead > tr > th, 
	& > tbody > tr > td {
		&:nth-child(2) {
			width: 10px;
			white-space: nowrap;
		}

		&:nth-child(3),
		&:nth-child(4),
		&:nth-child(5) {
			width: 10px;
			text-align: right;
			white-space: nowrap;
		}
	}

	& > tbody > tr > td {

		&:nth-child(3) {
			color: var(--bs-danger);
		}

		&:nth-child(4) {
			color: var(--bs-success);	
		}
	}
`;

let axiosCancelToken = null;

export default function DebtLedger() {
	const params = useParams();
	const propertyId = params.id;
	const [searchParams] = useSearchParams();

	const community = getCommunity();

	// Detectar modo owner
	const ownerId = searchParams.get('owner_id');
	const isOwnerMode = propertyId === 'null' && ownerId;

	const [data, setData] = useState(undefined);
	const [property, setProperty] = useState(undefined);
	const [showExportModal, setShowExportModal] = useState(false);
	const [skeletonRows, setSkeletonRows] = useState(5);
	const [financialYears, setFinancialYears] = useState([]);
	const [dateFrom, _setDateFrom] = useState(moment().startOf('year').format('YYYY-MM-DD'));
	const setDateFrom = (value) => {
		_setDateFrom(value);
		dateFromRef.current = value;
	}
	const [dateTo, _setDateTo] = useState(moment().endOf('year').format('YYYY-MM-DD'));
	const setDateTo = (value) => {
		_setDateTo(value);
		dateToRef.current = value;
	}
	const [applyDatesFix, _setApplyDatesFix] = useState(null);
	const setApplyDatesFix = (value) => {
		_setApplyDatesFix(value);
		setData(undefined);
	}
	const [loading, setLoading] = useState(false);

	// Estados para modo owner
	const [ownerProperties, setOwnerProperties] = useState([]);
	const [selectedProperty, setSelectedProperty] = useState({
		id: null,
		name: null
	});

	const dateFromRef = useRef(dateFrom);
	const dateToRef = useRef(dateTo);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	// Cargar propiedades del owner en modo owner
	useEffect(() => {
		if (!isOwnerMode || !community?.id || !ownerId) return;

		const loadOwnerProperties = async () => {
			await axios.get('/api/manager/properties/list', {
				params: {
					community_id: community.id,
					owner_id: ownerId,
					sort: 'name',
					direction: 'asc',
					no_paginate: true
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
				setOwnerProperties(response.data || []);
				// Seleccionar la primera propiedad por defecto
				if (response.data && response.data.length > 0) {
					setSelectedProperty(response.data[0]);
				}
			}).catch((error) => {
				if (axios.isCancel(error)) return;
			});
		};

		loadOwnerProperties();
	}, [isOwnerMode, community?.id, ownerId]);

	useEffect(() => {
		const currentPropertyId = isOwnerMode ? selectedProperty?.id : propertyId;
		if (!currentPropertyId || !community?.id) return;

		const getData = async () => {
			setLoading(true);

			await axios.get('/api/manager/properties/debt-ledger/' + currentPropertyId, {
				params: {
					community_id: community?.id,
					date_from: dateFromRef.current,
					date_to: dateToRef.current,
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setData([...response.data.debts || []]);
				setProperty(response.data.property);
		    	setSkeletonRows(response.data.debts?.length);
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});

			setLoading(false);
		}

		getData();
	}, [community?.id, propertyId, selectedProperty?.id, isOwnerMode, applyDatesFix]);

	useEffect(() => {
		const getFinancialYears = async () => {
			await axios.get('/api/manager/financial-years/list', {
				params: {
					community_id: community?.id,
					no_paginate: true
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
				setFinancialYears((response.data || []).slice(0, 5));
			}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});	
		}

		getFinancialYears();
	}, [community?.id]);

	const setDates = (e, range, additional) => {
		e.preventDefault();

		let {dateFrom, dateTo} = getDateRange(range, additional);
		
		setDateFrom(dateFrom);
		setDateTo(dateTo);
	}

	const getDateRange = (range, additional) => {
		let dateFrom = null;
		let dateTo = null;
		
		switch(range) {
			case 'thismonth':
				dateFrom = moment().startOf('month').format('YYYY-MM-DD');
				dateTo = moment().endOf('month').format('YYYY-MM-DD');
			break;

			case 'lastmonth':
				dateFrom = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
				dateTo = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
			break;

			case 'thisyear':
				dateFrom = moment().startOf('year').format('YYYY-MM-DD');
				dateTo = moment().endOf('year').format('YYYY-MM-DD');
			break;

			case 'lastyear':
				dateFrom = moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD');
				dateTo = moment().subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
			break;
			case 'year':
				if ( !additional ) return {dateFrom, dateTo};
				dateFrom = moment(additional, 'YYYY').startOf('year').format('YYYY-MM-DD');
				dateTo = moment(additional, 'YYYY').endOf('year').format('YYYY-MM-DD');
			break;	
			default: 
		}

		return {dateFrom, dateTo};;
	}

	const applyDates = () => {
		setApplyDatesFix(Math.random());
	}

	const loadProperties = (input, callback, args) => {
		// Formatear las propiedades ya cargadas
		let formatted = args.ownerProperties.map((el) => {
			return {
				value: el,
				searchText: (el.name + ' (' + el.number + ') ' + (el.owners?.map(o => o.name).join(' ') ?? '')).toLowerCase(),
				label: <div>
							{el.name} ({el.number})
							{el.owners?.length > 0 &&
								<div className="mt-1" style={{fontSize: '10px', color: '#5b9bd5'}}>{el.owners.map(o => o.name).join(', ')}</div>
							}
						</div>
			};
		});

		// Filtrar por input si existe
		if (input) {
			formatted = formatted.filter((item) =>
				item.searchText.includes(input.toLowerCase())
			);
		}

		// Simular async con setTimeout
		setTimeout(() => {
			callback(formatted);
		}, 0);
	}

	const handlePropertyChange = (value) => {
		setSelectedProperty(value);
		setData(undefined); // Resetear datos para mostrar skeleton
	}

	if ( !community?.id ) return <Navigate to="/" />;

	return (
		<PopupLayout>
			<section>
				<div className="page-title">
					<h1>Mayor de deuda</h1>
				</div>

				<div className="page-content">
					<div className="card border-0 shadow-sm">
						<div className="card-header bg-white">
							<div className="row align-items-center">
								<div className="col-md-3 mb-2 mt-2 mb-md-0 mt-md-0">
									{isOwnerMode ? (
										<EmpoweredSelector
											load={loadProperties}
											onChange={handlePropertyChange}
											args={{ownerProperties: ownerProperties}}
											timeout={250}
											label={selectedProperty?.name}
											placeholder="- Seleccionar propiedad -"
											value={selectedProperty?.id}
										/>
									) : (
										<b>{property?.name}</b>
									)}
								</div>
								<div className="col-md-4"></div>
								<div className="col-md-3 mb-2 mt-2 mb-md-0">
									<div className="input-group input-group-sm">
										<input type="date" className="form-control form-control-sm" placeholder="Desde" value={dateFrom ?? ''} onChange={(e) => setDateFrom(e.target.value)} />
										<input type="date" className="form-control form-control-sm" placeholder="Hasta" value={dateTo ?? ''} onChange={(e) => setDateTo(e.target.value)} />
										<span className="input-group-btn">
											<button className="btn btn-sm btn-light dropdown-toggle h-100" type="button" data-bs-toggle="dropdown" aria-expanded="false"></button>
											<ul className="dropdown-menu dropdown-menu-end">
												<li><a className="dropdown-item" href="." onClick={(e) => setDates(e, 'thismonth')}>Este mes</a></li>
												<li><a className="dropdown-item" href="." onClick={(e) => setDates(e, 'lastmonth')}>Mes pasado</a></li>
												<li><a className="dropdown-item" href="." onClick={(e) => setDates(e, 'thisyear')}>Este año</a></li>
												<li><a className="dropdown-item" href="." onClick={(e) => setDates(e, 'lastyear')}>Año pasado</a></li>
												{financialYears && financialYears.length > 0 &&
													<>
														<li><hr className="dropdown-divider" /></li>
														{ financialYears.map((year, idx) => {
															return (
																<li key={idx}>
																	<a className="dropdown-item" href="." onClick={(e) => setDates(e, 'year', year.year)}>{year.year}</a>
																</li>
															);
														})}
													</>
												}
											</ul>
										</span>
										<span className="input-group-btn">
											<button className="btn btn-sm btn-light h-100" disabled={loading} onClick={applyDates}>Aplicar</button>
										</span>
									</div>
								</div>
								<div className="col-md-1 text-end mb-2 mt-2 mb-md-0 mt-md-0">
									<button className="btn btn-sm btn-light" onClick={() => setShowExportModal(true)}>Exportar</button>
								</div>
							</div>
						</div>

						<div className="card-body p-0">
							<div className="table-responsive table-responsive-carded">
								<Table className="table table-hover table-sortable table-carded table-striped table-bordered">
									<thead>
										<tr>
											<th>Concepto</th>
											<th>Fecha</th>
											<th>Debe</th>
											<th>Haber</th>
											<th>Saldo</th>
										</tr>
									</thead>
									<tbody>
										{ (data?.length > 0) &&
											<>
												{data.map((el, idx) => {
													return ( 
														<tr key={idx}>
															<td>{el.concept}</td>
															<td>{moment(el.date).format('DD-MM-YYYY')}</td>
															<td>{el.amount < 0 ? formatNumber(el.amount, 2) : ''}</td>
															<td>{el.amount > 0 ? formatNumber(el.amount, 2) : ''}</td>
															<td className={el.balance < 0 ? 'text-danger' : 'text-success'}>{formatNumber(el.balance, 2)}</td>
														</tr>
													);
												})}
											</>
										}

										{ ((data && !data.length) && <tr><td colSpan="100%">No hay datos disponibles</td></tr>) }

										{ (data === undefined) && <TrSkeleton rows={skeletonRows} columns={5} /> }
									</tbody>
								</Table>
							</div>
						</div>
					</div>
				</div>
			</section>

			{ showExportModal &&
				<ExportModal
					exportKey={'properties/debt-ledger'}
					fileName={'Mayor de deuda ' + moment().format('DD-MM-YYYY HH:mm')}
					fieldsPlaceholder={<div>Selecciona una opción</div>}
					filters={{
						community_id: 				community?.id,
						property_id: 				isOwnerMode ? selectedProperty?.id : propertyId,
						date_from: 					dateFromRef.current,
						date_to: 					dateToRef.current,
					}}
					closeCallback={() => setShowExportModal(false)}
				/>
			}
		</PopupLayout>
	);
}


